import * as THREE from "three";
import type { KnightActor } from "./KnightActor";

/** Mother duck leads; three independently articulated ducklings trail the
 * local path direction. Kept procedural and allocation-free per frame. */
export class DuckFamilyActor implements KnightActor {
  readonly object=new THREE.Group();
  private mother=new THREE.Group();
  private motherHead=new THREE.Group();
  private children:THREE.Group[]=[];
  private childHeads:THREE.Group[]=[];
  private trail:THREE.Vector3[]=[];
  private targetYaw=0;
  private bodyYaw=0;
  private turnStartYaw=0;
  private turnDelta=0;
  private initialized=false;
  private geometries:THREE.BufferGeometry[]=[];
  private yellow=new THREE.MeshStandardMaterial({color:0xf1b82d,roughness:.58});
  private down=new THREE.MeshStandardMaterial({color:0xffcf4b,roughness:.72});
  private orange=new THREE.MeshStandardMaterial({color:0xe87924,roughness:.62});
  private dark=new THREE.MeshBasicMaterial({color:0x171410});

  constructor(single=false){
    this.object.name=single?"SingleDuckPerformer":"DuckFamilyPerformer";
    this.mother=this.makeDuck(single?1.04:.86,this.yellow,this.motherHead);
    // The head and beak put the visible centroid ahead of the actor pivot.
    // Re-center the complete mother mesh so a board-square coordinate means
    // the middle of the duck silhouette, especially in Top view.
    for(const part of this.mother.children)part.position.z-=.065;
    this.mother.position.set(0,.12,.12); this.object.add(this.mother);
    for(let i=0;i<(single?0:3);i++){
      const head=new THREE.Group(); this.childHeads.push(head);
      const child=this.makeDuck(.45,this.down,head); this.children.push(child);
      child.position.set((i%2?-.10:.09)*(i+1),.10,-.34-(i*.29));
      this.object.add(child);
    }
    // This root contains world-space trail coordinates and must remain 1:1
    // with the board. Scaling it used to pull every square toward (0,0).
    this.object.scale.setScalar(1);
    this.object.traverse(o=>{if((o as THREE.Mesh).isMesh)(o as THREE.Mesh).castShadow=true});
  }

  private makeDuck(scale:number,bodyMaterial:THREE.Material,headJoint:THREE.Group):THREE.Group{
    const g=new THREE.Group();
    const bodyG=new THREE.SphereGeometry(.22,18,12);bodyG.scale(.72,.56,1.05);this.geometries.push(bodyG);
    const body=new THREE.Mesh(bodyG,bodyMaterial);body.position.y=.12;g.add(body);
    headJoint.position.set(0,.28,.17);g.add(headJoint);
    const headG=new THREE.SphereGeometry(.145,16,10);this.geometries.push(headG);
    const head=new THREE.Mesh(headG,bodyMaterial);headJoint.add(head);
    const beakG=new THREE.ConeGeometry(.075,.17,4);beakG.rotateX(Math.PI/2);this.geometries.push(beakG);
    const beak=new THREE.Mesh(beakG,this.orange);beak.position.set(0,-.025,.15);headJoint.add(beak);
    const eyeG=new THREE.SphereGeometry(.018,7,5);this.geometries.push(eyeG);
    const l=new THREE.Mesh(eyeG,this.dark),r=new THREE.Mesh(eyeG,this.dark);l.position.set(-.085,.035,.105);r.position.set(.085,.035,.105);headJoint.add(l,r);
    const wingG=new THREE.SphereGeometry(.13,12,8);wingG.scale(.35,.45,1);this.geometries.push(wingG);
    const wl=new THREE.Mesh(wingG,bodyMaterial),wr=new THREE.Mesh(wingG,bodyMaterial);wl.position.set(-.15,.13,-.02);wr.position.set(.15,.13,-.02);g.add(wl,wr);
    g.scale.setScalar(scale);return g;
  }

  setPosition(p:THREE.Vector3):void{
    if(!this.initialized){
      this.initialized=true;this.mother.position.copy(p);
      for(let i=0;i<52;i++)this.trail.push(p.clone());
      this.children.forEach((c,i)=>c.position.copy(p).add(new THREE.Vector3((i%2?-.08:.08)*(i+1),0,-.25-i*.22)));
      return;
    }
    this.mother.position.copy(p);
    const latest=this.trail[this.trail.length-1];
    if(!latest||latest.distanceToSquared(p)>.0025){this.trail.push(p.clone());if(this.trail.length>90)this.trail.shift()}
  }
  setYaw(y:number):void{
    if(Math.abs(this.angleDelta(this.targetYaw,y))>.0001){
      this.turnStartYaw=this.bodyYaw;
      this.targetYaw=y;
      this.turnDelta=this.angleDelta(this.turnStartYaw,y);
    }
  }
  setPitch(p:number):void{this.mother.rotation.x=p*.42}
  setTurnPose(progress:number):void{
    // Head commits first; the heavier body follows during the latter half
    // of anticipation. No translation occurs in this phase.
    const headT=this.smooth(Math.min(1,progress/.34));
    const bodyT=this.smooth(Math.max(0,Math.min(1,(progress-.38)/.62)));
    this.bodyYaw=this.turnStartYaw+this.turnDelta*bodyT;
    // World-space head yaw reaches the target first; as the torso catches
    // up, the local neck rotation unwinds smoothly back toward neutral.
    const desiredHeadWorld=this.turnStartYaw+this.turnDelta*headT;
    this.motherHead.rotation.y=this.angleDelta(this.bodyYaw,desiredHeadWorld);
    this.mother.rotation.y=this.bodyYaw;
  }
  setSwimPose(pitch:number,roll:number,bend:number,tail:number,fin:number):void{
    this.bodyYaw+=this.angleDelta(this.bodyYaw,this.targetYaw)*.16;
    this.mother.rotation.y=this.bodyYaw+bend*.08;this.mother.rotation.x=pitch*.38;this.mother.rotation.z=roll*.34;
    this.motherHead.rotation.x=-pitch*.28+Math.sin(tail)*.04;this.motherHead.rotation.y=this.angleDelta(this.bodyYaw,this.targetYaw)*.5+Math.sin(fin)*.035;
    this.children.forEach((child,i)=>{
      const lag=i*.72;
      const historyLag=[25,34,44][i];
      const target=this.trail[Math.max(0,this.trail.length-1-historyLag)]??this.mother.position;
      const previous=child.position.clone();
      const speed=[.19,.135,.095][i];
      child.position.lerp(target,speed);
      child.position.x+=Math.sin(tail*1.7-lag)*(.008+i*.003);
      child.position.y=target.y+.015+Math.sin(tail*2-lag)*.025;
      const dx=child.position.x-previous.x,dz=child.position.z-previous.z;
      if(dx*dx+dz*dz>.000001)child.rotation.y=Math.atan2(dx,dz);
      child.rotation.y+=Math.sin(tail*1.5-lag)*.06;
      child.rotation.z=Math.sin(tail*1.25-lag)*.07;
      this.childHeads[i].rotation.x=-pitch*.18+Math.sin(tail*1.9-lag)*.08;
    });
    this.mother.position.y+=Math.sin(tail*1.4)*.006;
  }
  private angleDelta(from:number,to:number):number{return Math.atan2(Math.sin(to-from),Math.cos(to-from))}
  private smooth(t:number):number{return t*t*(3-2*t)}
  dispose():void{this.geometries.forEach(g=>g.dispose());this.yellow.dispose();this.down.dispose();this.orange.dispose();this.dark.dispose();this.object.clear()}
}

export class SingleDuckActor extends DuckFamilyActor {
  constructor(){super(true)}
}
